import React, { useState, useCallback, useRef } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Menu,
  MenuItem,
} from "@mui/material";
import api from "@/data/api";
import Toast from "@/utils/toastManager";

export function ProductsDropdown({ value = [], onChange }) {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [anchor, setAnchor] = useState(null);
  const [variantAnchor, setVariantAnchor] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const menuButtonRef = useRef(null);

  const handleOpen = useCallback(
    async (event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      setAnchor(menuButtonRef.current);
      if (!products.length && !isLoading) {
        setIsLoading(true);
        try {
          const res = await api.searchProducts(query);
          setProducts(res?.data || []);
        } catch {
          Toast.error("Failed to fetch products")
        } finally {
          setIsLoading(false);
        }
      }
    },
    [products.length, isLoading, query]
  );

  const handleClose = () => {
    setAnchor(null);
    handleVariantClose();
  };

  const handleVariantClose = () => {
    setVariantAnchor(null);
    setSelectedProduct(null);
  };

  const handleProductClick = useCallback((event, product) => {
    if (product.variants?.length) {
      setSelectedProduct(product);
      setVariantAnchor(event.currentTarget);
    } else {
      handleAddProduct(product);
    }
  }, []);

  const handleAddProduct = useCallback(
    (product, variant = null) => {
      const alreadyExists = value.some(
        (v) =>
          v.title === product.title &&
          (variant ? v.variant?.id === variant.id : !v.variant)
      );
      if (alreadyExists) return;

      const newItem = variant
        ? { _id: product._id, id: product.id, title: product.title, variant }
        : { _id: product._id, id: product.id, title: product.title };

      onChange([...value, newItem]);
    },
    [value, onChange]
  );

  const handleRemove = useCallback(
    (index) => {
      const updated = [...value];
      updated.splice(index, 1);
      onChange(updated);
    },
    [value, onChange]
  );

  return (
    <Box
      sx={{
        mb: 2,
        p: 2.5,
        backgroundColor: "#fff",
        borderRadius: 2,
        boxShadow: 2,
        maxWidth: "100%",
        mx: "auto",
      }}
    >
      <Typography
        variant="h6"
        sx={{ mb: 1, fontWeight: 500, color: "text.primary" }}
      >
        Products
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          minHeight: 40,
          border: "0.06rem solid #e0e0e0",
          p: 1,
          borderRadius: 1,
          mb: 2,
        }}
      >
        {value.length ? (
          value.map((item, idx) => (
            <Chip
              key={idx}
              label={
                item.variant || item?.variantTitle
                  ? `${item.title} — ${item.variant?.title || item?.variantTitle}`
                  : item.title || item
              }
              onDelete={() => handleRemove(idx)}
              variant="outlined"
            />
          ))
        ) : (
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", alignSelf: "center" }}
          >
            No products selected.
          </Typography>
        )}
      </Box>
      <Box>
        <button
          ref={menuButtonRef}
          onClick={handleOpen}
          disabled={isLoading}
          style={{
            background: "#1976d2",
            color: "#fff",
            border: "none",
            borderRadius: "0.375rem",
            padding: "0.5rem 1rem",
            cursor: "pointer",
            fontWeight: 500,
            boxShadow: "0 0.12rem 0.25rem rgba(0,0,0,0.2)",
            transition: "background-color 0.2s",
          }}
          onMouseOver={(e) =>
            (e.currentTarget.style.backgroundColor = "#1565c0")
          }
          onMouseOut={(e) =>
            (e.currentTarget.style.backgroundColor = "#1976d2")
          }
        >
          {isLoading ? "Loading..." : "+ Add Product"}
        </button>
      </Box>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {isLoading ? (
          <MenuItem disabled>Loading products...</MenuItem>
        ) : products.length === 0 ? (
          <MenuItem disabled>No products found.</MenuItem>
        ) : (
          products.map((product) => (
            <MenuItem
              key={product._id}
              onClick={(e) => handleProductClick(e, product)}
              sx={{ display: "flex", justifyContent: "space-between" }}
            >
              <span>{product.title}</span>
              {product.variants?.length > 0 && (
                <ChevronRightIcon size={16} color="#757575" />
              )}
            </MenuItem>
          ))
        )}
      </Menu>
      <Menu
        anchorEl={variantAnchor}
        open={Boolean(variantAnchor)}
        onClose={handleVariantClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {selectedProduct?.variants?.map((variant) => (
          <MenuItem
            key={variant.id}
            onClick={() => handleAddProduct(selectedProduct, variant)}
          >
            {variant?.title}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
